/* eslint-disable max-statements */
import type { Kitchen } from '@rta/rta-blueprint-library';

export type GetCountertopLabelParams = {
	kitchen: Kitchen;
};

/**
 * Formats the countertop material to match the PDF blueprint format.
 *
 * PDF format examples (derived from scanning ~9k blueprint PDFs):
 *   "Concrete | Chiseled Edge | Cloudy | Seams Required"
 *   "Concrete | Clean Edge | Smoky"
 *   "Granite | Thunder White | Seams Required"
 *   "MOKS | Dove Shimmer"                       (MOKS never carries a seam suffix)
 *   "No Countertops"                            (key = 'none')
 *
 * Source displayName format (from countertop-asset.map.json):
 *   "Chiseled Edge - Cloudy" / "Clean Edge - Smoky"  (concrete, edge: "chiseled" | "clean")
 *   "Granite - Thunder White"                         (granite, edge: null)
 *   "MOKS - Dove Shimmer"                             (MOKS, edge: "clean")
 *   "No Countertop"                                   (special key)
 *
 * Seam suffix (SOP §10A):
 *   - 1 seam  → "Seam Required", 2+ seams → "Seams Required"
 *   - 0 seams → "No Seams" (omitted for the 'none' key)
 *   - MOKS tops never carry any seam suffix
 */
const EDGE_LABELS: Record<string, string> = {
	chiseled: 'Chiseled Edge',
	clean: 'Clean Edge'
};

// All RTA granite countertops ship with a leathered finish today; no per-color/catalog data exists to vary this.
export const GRANITE_FINISH = 'Leathered';

const parseDisplayName = (displayName: string): { prefix: string; color: string } => {
	const parts = displayName.split(/\s*-\s*/);
	if (parts.length < 2) return { prefix: '', color: displayName };
	const color = parts[parts.length - 1];
	const prefix = parts.slice(0, -1).join(' - ');
	return { prefix, color };
};

const countSeams = (kitchen: Kitchen): number => {
	const { countertops } = kitchen.design;
	return countertops.children.reduce((sum, ct) => sum + ct.breakpoints.children.length, 0);
};

// Round to 2 decimals, trim trailing zeros so 1.25 → "1.25" and 1.5 → "1.5".
const formatThickness = (value: number): string => (Math.round(value * 100) / 100).toString();

export const getCountertopLabel = (params: GetCountertopLabelParams): string => {
	const { kitchen } = params;
	const material = kitchen.material?.countertop;
	const displayName = material?.displayName ?? '';
	const key = material?.key ?? '';

	// Special-case keys that don't follow the standard "{type} - {color}" naming.
	// Material "none" = RTA does not provide the countertop; it is templated on-site.
	if (key === 'none') return 'N/A | Not Provided by RTA | Countertop Must Be Templated Onsite';

	if (!displayName) return 'N/A';

	const isGranite = material?.isGranite ?? false;
	const edge = material?.edge ?? null;
	const { prefix, color } = parseDisplayName(displayName);
	const isMok = prefix.toUpperCase().startsWith('MOK');

	const parts: Array<string> = [];

	if (isMok) {
		parts.push('MOKS', color);
	} else if (isGranite) {
		parts.push('Granite', color, GRANITE_FINISH);
	} else if (edge && EDGE_LABELS[edge] && prefix.toLowerCase().includes('edge')) {
		parts.push('Concrete', EDGE_LABELS[edge], color);
	} else if (prefix) {
		parts.push(prefix, color);
	} else {
		parts.push(color);
	}

	// Suffix is singular "Seam Required" for exactly one seam, plural "Seams Required" for two or more.
	// MOKS tops never carry a seam suffix (no "No Seams", no "Seam(s) Required").
	if (!isMok) {
		const seams = countSeams(kitchen);
		if (seams > 0) {
			parts.push(seams === 1 ? 'Seam Required' : 'Seams Required');
		} else if (key !== 'none') {
			parts.push('No Seams');
		}
	}

	const thickness = material?.thickness;
	if (typeof thickness === 'number' && thickness > 0) {
		parts.push(`Thickness – ${formatThickness(thickness)}"`);
	}

	return parts.join(' | ');
};
